import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {FabricObject, FabricText} from 'fabric';
import {
    Box,
    IconButton,
    Stack,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    ArrowBack,
    ContentCopy,
    Palette,
    Settings,
    Straighten,
    TextFields,
} from '@mui/icons-material';
import {TableSettingsPanel} from './TableSettingsPanel';
import {EditTableStyles} from './EditTableStyles';
import {EditTextStyles} from './EditTextStyles';
import {EditShapeStyles} from './EditShapeStyles';
import {EditWallStyles} from './EditWallStyles';
import {OctagonShapeIcon} from '../shared/OctagonShapeIcon';
import {HexagonShapeIcon} from '../shared/HexagonShapeIcon';
import {TriangleShapeIcon} from '../shared/TriangleShapeIcon';
import {CircleShapeIcon} from '../shared/CircleShapeIcon';
import {EllipseShapeIcon} from '../shared/EllipseShapeIcon';
import {RectangleIcon} from '../shared/RectangleIcon';
import {PreviewTableIcon} from './PreviewTableIcon';

type ElementType = 'table' | 'text' | 'shape' | 'wall';

const getElementType = (element: FabricObject): ElementType => {
    if (element.floorPlanItem?.table) return 'table';
    if (element instanceof FabricText && element.floorPlanItem?.element?.kind === 'text') return 'text';
    if (element.floorPlanItem?.element?.kind === 'shape') return 'shape';
    return 'wall';
};

interface Props {
    element: FabricObject;
    onBack: () => void;
    onElementUpdated: () => void;
    onDuplicate?: () => void;
}

export const EditPanel: React.FC<Props> = ({
                                               element,
                                               onBack,
                                               onElementUpdated,
                                               onDuplicate,
                                           }) => {
    const {t} = useTranslation();
    const [activeTab, setActiveTab] = useState(
        () => Number(localStorage.getItem('floorplanEditTab')) || 0
    );

    const item = element.floorPlanItem;
    const tableData = item?.table;
    const elementData = item?.element;
    const elementType = getElementType(element);


    const renderHeader = () => {
        let icon: React.ReactNode;
        let title: string;

        if (elementType === "table") {
            icon = <PreviewTableIcon item={item!}/>;
            title = tableData?.resource?.name ?? t('Table');
        } else if (elementType === "text") {
            icon = <TextFields fontSize="small"/>;
            title = t('Text');
        } else if (elementType === "shape") {
            const shape = elementData?.kind === "shape" ? elementData.shape : undefined;
            const shapeLabel = (elementData?.canvas as any)?.label;

            if (shape === "circle") {
                icon = <CircleShapeIcon fontSize="small"/>;
                title = shapeLabel || t('Circle');
            } else if (shape === "ellipse") {
                icon = <EllipseShapeIcon fontSize="small"/>;
                title = shapeLabel || t('Ellipse');
            } else if (shape === "triangle") {
                icon = <TriangleShapeIcon fontSize="small"/>;
                title = shapeLabel || t('Triangle');
            } else if (shape === "hexagon") {
                icon = <HexagonShapeIcon fontSize="small"/>;
                title = shapeLabel || t('Hexagon');
            } else if (shape === "octagon") {
                icon = <OctagonShapeIcon fontSize="small"/>;
                title = shapeLabel || t('Octagon');
            } else {
                icon = <RectangleIcon fontSize="small"/>;
                title = shapeLabel || t('Rectangle');
            }
        } else {
            icon = <Straighten fontSize="small"/>;
            const wallLabel = (elementData?.canvas as any)?.label;
            title = wallLabel || t('Wall');
        }

        return (
            <Box sx={{p: 2, pb: 1, borderBottom: 1, borderColor: "divider"}}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Tooltip title={t('Back to list')}>
                        <IconButton size="small" onClick={onBack}>
                            <ArrowBack/>
                        </IconButton>
                    </Tooltip>
                    {icon}
                    <Typography variant="h6" noWrap sx={{flex: 1}}>
                        {title}
                    </Typography>
                    {onDuplicate && (
                        <Tooltip title={t('Duplicate')}>
                            <IconButton size="small" onClick={onDuplicate}>
                                <ContentCopy fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
            </Box>
        );
    };


    return (
        <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
            {renderHeader()}

            {elementType === 'table' && tableData && (
                <>
                    <Tabs
                        value={activeTab}
                        onChange={(_, v) => { localStorage.setItem('floorplanEditTab', String(v)); setActiveTab(v); }}
                        variant="fullWidth"
                        sx={{borderBottom: 1, borderColor: 'divider'}}
                    >
                        <Tab icon={<Settings/>} label={t('Settings')}
                             iconPosition="start" sx={{minHeight: 48}}/>
                        <Tab icon={<Palette/>} label={t('Style')}
                             iconPosition="start"
                             sx={{minHeight: 48}}/>
                    </Tabs>

                    <Box sx={{flex: 1, overflow: 'auto', p: 2}}>
                        {activeTab === 0 && (
                            <TableSettingsPanel
                                element={element}
                                onElementUpdated={onElementUpdated}
                            />
                        )}
                        {activeTab === 1 && (
                            <EditTableStyles
                                element={element}
                                onBack={onBack}
                                onElementUpdated={onElementUpdated}
                            />
                        )}
                    </Box>
                </>
            )}

            {elementType === 'text' && elementData && (
                <Box sx={{flex: 1, overflow: 'auto', p: 2}}>
                    <EditTextStyles
                        element={element}
                        onBack={onBack}
                        onElementUpdated={onElementUpdated}
                    />
                </Box>
            )}

            {elementType === 'shape' && elementData && (
                <Box sx={{flex: 1, overflow: 'auto', p: 2}}>
                    <EditShapeStyles
                        element={element}
                        onBack={onBack}
                        onElementUpdated={onElementUpdated}
                    />
                </Box>
            )}

            {elementType === 'wall' && elementData && (
                <Box sx={{flex: 1, overflow: 'auto', p: 2}}>
                    <EditWallStyles
                        element={element}
                        onBack={onBack}
                        onElementUpdated={onElementUpdated}
                    />
                </Box>
            )}
        </Box>
    );
};
